import { type OrgaMemberRole, SystemRole } from "@/lib/auth/auth.types";
import { useAuth } from "@/lib/contexts/auth.context";
import { Navigate, useLocation } from "react-router-dom";
import LoadingSpinner from "./loadingSpinner.component";

/**
 * This component is used to require the user to be authenticated to access the route.
 * If the user is not authenticated, they will be redirected to the login page.
 * If the user is authenticated, they will be able to access the route.
 *
 * @param children
 * @returns
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
	const { isAuthenticated, isLoading } = useAuth();
	const location = useLocation();

	if (isLoading) {
		return <LoadingSpinner />; // Or your loading component
	}

	if (!isAuthenticated) {
		return <Navigate to="/auth/login" state={{ from: location }} replace />;
	}

	// something breaks here, this is being access and yes, the user is authenticated, but then suddenly the user is not authenticated and returns to the login page
	return <>{children}</>;
}

/**
 * This component is used to require the user to be not authenticated to access the route.
 * If the user is authenticated, they will be redirected to the dashboard.
 * If the user is not authenticated, they will be able to access the route.
 * So kind of the opposite of RequireAuth.
 *
 * @param children
 * @returns
 */
export function IsNotAuthenticated({
	children,
}: {
	children: React.ReactNode;
}) {
	const { isAuthenticated, isLoading } = useAuth();
	const location = useLocation();

	if (isLoading) {
		return <LoadingSpinner />; // Or your loading component
	}

	if (isAuthenticated) {
		return <Navigate to="/dashboard" state={{ from: location }} replace />;
	}

	return <>{children}</>;
}

/**
 * This component is used to require the user to be authenticated and be a platform manager to access the route.
 * If the user is not authenticated or not a platform manager, they will be redirected to the dashboard.
 *
 * @param children
 * @returns
 */
export function RequirePlatformManager({
	children,
}: {
	children: React.ReactNode;
}) {
	const { isAuthenticated, isLoading, user } = useAuth();
	const location = useLocation();

	if (!isAuthenticated) {
		return <Navigate to="/auth/login" state={{ from: location }} replace />;
	}

	if (isLoading) {
		return <LoadingSpinner />; // Or your loading component
	}

	// if the user is not a platform manager, redirect to the dashboard
	if (!user?.systemRoles.includes(SystemRole.PLATFORM_MANAGER)) {
		return <Navigate to="/dashboard" state={{ from: location }} replace />;
	}

	// if the user is a platform manager, show the info banner and the children
	return <>{children}</>;
}

/**
 * This component is used to require the user to have a specific orga member role to access the route.
 * If the user does not have the required role, they will be redirected to the login page.
 * If the user has the required role, they will be able to access the route.
 *
 * TODO: We need to validate on backend side if the user has the required role in the specific orga.
 * This should be done by the backend.
 *
 * TODO: We could add the orga id as parameter to the component to make it more flexible.
 *
 * @param children
 * @param role (orga member role)
 */
export function RequireOrgaMemberRole({
	children,
	role,
}: {
	children: React.ReactNode;
	role: OrgaMemberRole;
}) {
	const { isAuthenticated, isLoading } = useAuth();
	const location = useLocation();

	if (!role) {
		throw new Error("Role is required");
	}

	if (!isAuthenticated) {
		return <Navigate to="/auth/login" state={{ from: location }} replace />;
	}

	if (isLoading) {
		return <LoadingSpinner />;
	}

	// TODO: Check if the user has the required role in any of their organizations
	// This is not implemented yet

	return <>{children}</>;
}
